import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { fmt, fmtDate, fmtTime, numWords, fmtQty, COMPANY } from '../utils/format';
import { LogoDark } from './Logo';
import { downloadInvoicePdf } from '../utils/generateInvoicePdf';
import { printInvoice } from '../utils/printInvoice';
import Alert from './Alert';

export default function InvoiceViewModal({ invoiceId, onClose }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    api
      .get(`/invoices/${invoiceId}`)
      .then((res) => {
        if (mounted) setInvoice(res.data.invoice);
      })
      .catch(() => setError('Could not load invoice.'))
      .finally(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, [invoiceId]);

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" style={{ width: 680 }}>
        <div className="modal-header">
          <div className="modal-title">Invoice Preview {invoice ? `— ${invoice.invoice_number}` : ''}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {invoice && (
              <>
                <button className="btn btn-sm btn-primary" onClick={() => printInvoice(invoice)}>
                  <i className="ti ti-printer" aria-hidden="true"></i>Print
                </button>
                <button className="btn btn-sm btn-orange" onClick={() => downloadInvoicePdf(invoice)}>
                  <i className="ti ti-download" aria-hidden="true"></i>PDF
                </button>
              </>
            )}
            <button className="btn btn-sm" onClick={onClose}>
              <i className="ti ti-x" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        <div className="modal-body">
          {loading && <div className="empty-state">Loading invoice...</div>}
          <Alert type="danger" message={error} onClose={() => setError('')} />
          {invoice && <InvoiceDocument invoice={invoice} />}
        </div>
      </div>
    </div>
  );
}

export function InvoiceDocument({ invoice }) {
  const subtotal = invoice.subtotal ?? (invoice.items || []).reduce((s, i) => s + parseFloat(i.line_total ?? i.total ?? 0), 0);
  const tax = invoice.tax || 0;
  const discount = invoice.discount || 0;
  const total = invoice.total;

  const previousDue = parseFloat(invoice.previous_due || 0);
  const amountPaid = invoice.amount_paid != null ? parseFloat(invoice.amount_paid) : null;
  const balanceDue = parseFloat(invoice.balance_due || 0);
  // Only surface the payment breakdown when it's not a plain paid-in-full sale.
  const showPayment = amountPaid != null && (previousDue > 0 || balanceDue > 0 || amountPaid < parseFloat(total));

  return (
    <>
      <div className="inv-header">
        <div className="inv-title-block">
          <div className="inv-title">INVOICE</div>
          <div className="inv-num"># {invoice.invoice_number}</div>
          <div className="inv-date">Date: {fmtDate(invoice.invoice_date)}</div>
          <div className="inv-date">Time: {fmtTime(invoice.invoice_date)}</div>
        </div>
        <div className="inv-brand">
          <LogoDark size={135} />
          <div className="inv-brand-email">{COMPANY.email}</div>
        </div>
      </div>

      <div className="inv-addresses">
        <div>
          <div className="inv-addr-label">Bill To</div>
          <div className="inv-addr-name">{invoice.customer_name || 'Walk-in Customer'}</div>
          <div className="inv-addr-detail">
            {invoice.customer_address || ''}
            <br />
            {invoice.customer_phone ? `Phone: ${invoice.customer_phone}` : ''}
          </div>
        </div>
        <div>
          <div className="inv-addr-label">Bill From</div>
          <div className="inv-addr-name">{COMPANY.name}</div>
          <div className="inv-addr-detail">
            {COMPANY.address}
            <br />
            {COMPANY.city}
            <br />
            Phone: {COMPANY.phone}
            <br />
            GST: {invoice.gst_number || '—'}
          </div>
        </div>
      </div>

      <div className="inv-meta">
        <div className="inv-meta-cell">
          <div className="inv-meta-label">Order Date</div>
          <div className="inv-meta-val">{fmtDate(invoice.invoice_date)}</div>
        </div>
        <div className="inv-meta-cell">
          <div className="inv-meta-label">Order Time</div>
          <div className="inv-meta-val">{fmtTime(invoice.invoice_date)}</div>
        </div>
        <div className="inv-meta-cell">
          <div className="inv-meta-label">Order By</div>
          <div className="inv-meta-val" style={{ fontSize: 10 }}>
            {invoice.customer_name || 'Walk-in'}
            <br />
            {invoice.customer_phone ? `(${invoice.customer_phone})` : ''}
          </div>
        </div>
      </div>

      <table className="inv-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Item</th>
            <th>MRP</th>
            <th>Base Pack</th>
            <th>Qty</th>
            <th>Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {(invoice.items || []).map((it, i) => (
            <tr key={i}>
              <td>{i + 1}</td>
              <td style={{ fontWeight: 600 }}>{it.product_name || it.name}</td>
              <td>Rs. {parseFloat(it.mrp).toFixed(2)}</td>
              <td>{it.base_pack ?? it.basePack}</td>
              <td>{fmtQty(it.qty, it.unit)}</td>
              <td>Rs. {parseFloat(it.price).toFixed(2)}</td>
              <td>Rs. {parseFloat(it.line_total ?? it.total).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', marginBottom: 4 }}>
            Total Amount in Words
          </div>
          <div className="inv-words">{numWords(total)}</div>
          <div style={{ color: 'var(--green)', fontWeight: 700, fontSize: 12, marginTop: '.5rem' }}>
            THANK YOU FOR SHOPPING WITH EASY1BASKET!
          </div>
        </div>
        <div className="inv-totals-box">
          <div className="inv-total-row">
            <span>Sub Total</span>
            <span>{fmt(subtotal)}</span>
          </div>
          <div className="inv-total-row">
            <span>Discount</span>
            <span>Rs. {parseFloat(discount).toFixed(2)}</span>
          </div>
          <div className="inv-total-row">
            <span>Tax (GST{invoice.tax_rate ? ` ${parseFloat(invoice.tax_rate).toFixed(2)}%` : ''})</span>
            <span>{fmt(tax)}</span>
          </div>
          <div className="inv-grand">
            <span>Grand Total</span>
            <span>{fmt(total)}</span>
          </div>
          {showPayment && (
            <>
              {previousDue > 0 && (
                <>
                  <div className="inv-total-row">
                    <span>Previous Dues</span>
                    <span>{fmt(previousDue)}</span>
                  </div>
                  <div className="inv-total-row">
                    <span>Total Payable</span>
                    <span>{fmt(parseFloat(previousDue) + parseFloat(total))}</span>
                  </div>
                </>
              )}
              <div className="inv-total-row">
                <span>Amount Paid</span>
                <span>{fmt(amountPaid)}</span>
              </div>
              <div className="inv-total-row" style={{ fontWeight: 700, color: balanceDue > 0 ? 'var(--danger, #e53935)' : 'var(--green)' }}>
                <span>Balance Due</span>
                <span>{fmt(balanceDue)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="inv-footer-addr" style={{ marginTop: '1rem' }}>
        <strong>{COMPANY.name}</strong> | {COMPANY.address}, {COMPANY.city} | Phone: {COMPANY.phone} | GST:{' '}
        {invoice.gst_number || '—'}
      </div>
    </>
  );
}
